import React from "react";
import {
  CalendarClock,
  PauseCircle,
  CheckCircle2,
  PlayCircle,
  Clock,
  AlertCircle,
  Loader2,
  Wand2,
  Timer,
} from "lucide-react";

export const getPostStatusInfo = (
  post: any,
  task: any,
  isFutureTask?: boolean,
) => {
  // If this is a future task occurrence and no post exists yet, show "Scheduled"
  if (isFutureTask && !post) {
    return {
      label: "Scheduled",
      icon: <CalendarClock className="w-5 h-5 text-blue-500" />,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
    };
  }

  // Check if post is published by looking at multiple indicators
  if (
    post?.status === "published" ||
    post?.platform_post_id ||
    post?.platform_post_url ||
    post?.metadata?.platform_post_id ||
    post?.metadata?.platform_post_url
  ) {
    return {
      label: "Published",
      icon: <CheckCircle2 className="w-5 h-5 text-green-500 fill-current" />,
      color: "text-green-500",
      bgColor: "bg-green-50",
    };
  }

  // Handle ready_posts table entries first (highest priority)
  if (post?.table === "ready_posts" && post?.is_default) {
    return {
      label: "Default Ready",
      icon: <PlayCircle className="w-5 h-5 text-purple-500 fill-current" />,
      color: "text-purple-500",
      bgColor: "bg-purple-50",
    };
  }

  // Handle ready_posts table entries that are not default — awaiting auto-promotion
  // Skip superseded posts (old posts replaced by newer defaults)
  if (post?.table === "ready_posts" && !post?.is_default && post?.status !== "superseded") {
    return {
      label: "Ready (Awaiting)",
      icon: <Timer className="w-5 h-5 text-amber-500 animate-pulse" />,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    };
  }

  // Handle scheduled status for future recurring posts
  if (post?.status === "scheduled") {
    return {
      label: "Scheduled",
      icon: <CalendarClock className="w-5 h-5 text-blue-500" />,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
    };
  }

  if (!post) {
    if (task.status === "active") {
      const scheduledTime = new Date(task.scheduled_for);
      const now = new Date();
      const isPastDue = scheduledTime < now;

      if (isPastDue) {
        return {
          label: "Processing",
          icon: <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />,
          color: "text-orange-700",
          bgColor: "bg-orange-50",
        };
      } else {
        return {
          label: "Scheduled",
          icon: <CalendarClock className="w-5 h-5 text-blue-500" />,
          color: "text-blue-500",
          bgColor: "bg-blue-50",
        };
      }
    } else {
      return {
        label: "Paused",
        icon: <PauseCircle className="w-5 h-5 text-orange-500" />,
        color: "text-orange-500",
        bgColor: "bg-orange-50",
      };
    }
  }

  // Handle error state first
  if (post.status === "error") {
    return {
      label: "Error",
      icon: <AlertCircle className="w-5 h-5 text-red-500" />,
      color: "text-red-500",
      bgColor: "bg-red-50",
    };
  }

  // Handle ready state - check if it's a default ready post
  if (post.status === "ready") {
    // Check if this post is marked as default ready (from is_default_ready prop or metadata)
    const isDefaultReady =
      post.is_default_ready ||
      post.isDefaultReady ||
      post.is_default === true ||
      post.metadata?.is_default_ready === true;

    if (isDefaultReady) {
      return {
        label: "Default Ready",
        icon: <PlayCircle className="w-5 h-5 text-purple-500 fill-current" />,
        color: "text-purple-500",
        bgColor: "bg-purple-50",
      };
    }

    return {
      label: "Ready (Awaiting)",
      icon: <Timer className="w-5 h-5 text-amber-500 animate-pulse" />,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    };
  }

  // Handle pending states with stages
  if (post.status === "pending") {
    // Check if post is in progress
    const isInProgress = post.metadata?.is_in_progress === true;
    const stage = post.metadata?.stage;

    if (isInProgress) {
      switch (stage) {
        case "publishing":
          return {
            label: "Publishing",
            icon: <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />,
            color: "text-indigo-700",
            bgColor: "bg-indigo-50",
          };
        case "creation":
          return {
            label: "Creating",
            icon: <Wand2 className="w-5 h-5 text-orange-500 animate-pulse" />,
            color: "text-orange-700",
            bgColor: "bg-orange-50",
          };
        case "webhook_processing":
          return {
            label: "Processing",
            icon: <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />,
            color: "text-orange-700",
            bgColor: "bg-orange-50",
          };
        default:
          return {
            label: "Processing",
            icon: <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />,
            color: "text-orange-700",
            bgColor: "bg-orange-50",
          };
      }
    }
  }

  // Default pending state
  return {
    label: "Pending",
    icon: <Clock className="w-5 h-5 text-gray-500" />,
    color: "text-gray-500",
    bgColor: "bg-gray-50",
  };
};

interface PostStatusProps {
  post: any;
  task: any;
  isPublishing?: boolean;
  publishProgress?: string | null;
  isFutureTask?: boolean;
}

export const PostStatus: React.FC<PostStatusProps> = ({
  post,
  task,
  isPublishing,
  publishProgress,
  isFutureTask,
}) => {
  const status = getPostStatusInfo(post, task, isFutureTask);

  return (
    <div className="flex items-center gap-2">
      {status.icon}
      <span className={`text-sm font-medium ${status.color}`}>
        {status.label}
      </span>
      {isPublishing && publishProgress && (
        <div className="flex items-center gap-2 text-blue-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">{publishProgress}</span>
        </div>
      )}
    </div>
  );
};
